import { Component, createRef, type ReactNode, type RefObject } from "react";

type Props = {
  children: (root: RefObject<HTMLDivElement>) => ReactNode;
};
type Snapshot = { anchor: number; focus: number; selected: string };

function textNodes(root: Node) {
  return document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest("[data-chat-citation]")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
}

function sourceText(root: Node) {
  const walker = textNodes(root);
  let text = "";
  for (let node = walker.nextNode(); node; node = walker.nextNode())
    text += node.textContent ?? "";
  return text;
}

function sourceOffset(root: Node, node: Node, offset: number) {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(node, offset);
  const fragment = range.cloneContents();
  fragment.querySelectorAll("[data-chat-citation]").forEach((badge) => {
    badge.remove();
  });
  return fragment.textContent?.length ?? 0;
}

function positionAt(root: Node, offset: number) {
  const walker = textNodes(root);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const length = node.textContent?.length ?? 0;
    if (offset <= length) return { node, offset };
    offset -= length;
  }
  return null;
}

// Markdown can split a selected text leaf when late metadata adds a citation.
// Snapshot before DOM mutation; restore after child layout effects populate the
// new leaves. Ordinary streaming without a selection never walks the text.
export class PreserveMarkdownSelection extends Component<Props> {
  private root = createRef<HTMLDivElement>();

  getSnapshotBeforeUpdate(): Snapshot | null {
    const root = this.root.current;
    const selection = window.getSelection();
    if (
      !root ||
      !selection ||
      selection.isCollapsed ||
      !selection.anchorNode ||
      !selection.focusNode ||
      !root.contains(selection.anchorNode) ||
      !root.contains(selection.focusNode)
    )
      return null;
    const anchor = sourceOffset(
      root,
      selection.anchorNode,
      selection.anchorOffset,
    );
    const focus = sourceOffset(
      root,
      selection.focusNode,
      selection.focusOffset,
    );
    return {
      anchor,
      focus,
      selected: sourceText(root).slice(
        Math.min(anchor, focus),
        Math.max(anchor, focus),
      ),
    };
  }

  componentDidUpdate(
    _previousProps: Props,
    _previousState: unknown,
    snapshot: Snapshot | null,
  ) {
    const root = this.root.current;
    if (!root || !snapshot?.selected) return;
    const { anchor, focus, selected } = snapshot;
    // A replacement response may contain different text at those positions.
    if (
      sourceText(root).slice(
        Math.min(anchor, focus),
        Math.max(anchor, focus),
      ) !== selected
    )
      return;
    const start = positionAt(root, anchor);
    const end = positionAt(root, focus);
    if (start && end)
      window
        .getSelection()
        ?.setBaseAndExtent(start.node, start.offset, end.node, end.offset);
  }

  render() {
    return this.props.children(this.root);
  }
}
